
import React, { memo } from "react";
import ButtonComponent from "../../components/ButtonMC";
import EmptyScreenListInboxMC from "../../components/EmptyScreenListInboxMC";
import CustomerDealItemMC from "./components/CustomerDealItemMC";
import LoadingMC from "../../components/LoadingMC";
import useCustomerFunnelsData from "../../hooks/inbox/data/useCustomerFunnelsData";
import useCustomerFunnelsUI from "../../hooks/inbox/ui/useCustomerFunnelsUI";

/**
 * Componente Organismo optimizado para mostrar y gestionar negociaciones (deals) del cliente en el Inbox.
 *
 * @component CustomerFunnelsMC
 * @category Component - Organism
 * @description
 * Organismo visual que muestra la lista de negociaciones (deals) de un cliente, permite crear nuevas y editar existentes.
 * Utiliza hooks especializados para separar lógica de negocio (data/actions) y presentación (UI), siguiendo arquitectura y performance recomendadas.
 *
 * **Características:**
 * - Carga y renderizado de negociaciones del cliente
 * - Botón para crear nueva negociación
 * - Renderizado de cada negociación con CustomerDealItemMC
 * - Mensaje vacío si no hay negociaciones
 * - Optimización de performance con React.memo y hooks especializados
 *
 * **Atomic Design:**
 * - Nivel: Organism
 * - Composición: utiliza ButtonComponent, EmptyScreenListInboxMC, CustomerDealItemMC, LoadingMC
 *
 * @param {Object} props
 * @param {Object} props.customerData - Objeto con datos del customer (customer: {id, ...})
 *
 * @returns {JSX.Element} Elemento visual renderizado
 *
 * @example
 * <CustomerFunnelsMC customerData={customerData} />
 *
 * @see useCustomerFunnelsData Hook de lógica de negocio
 * @see useCustomerFunnelsUI Hook de UI y traducciones
 * @see CustomerDealItemMC Componente de item de negociación
 *
 * @since 3.1.0
 * @version 3.1.2 - JSDoc completo y optimización
 */
const CustomerFunnelsMC = memo(({ customerData }) => {
  // Data y lógica de negocio (Data/Actions Layer)
  const {
    customerDeals,
    loading,
    reloadDeals,
    openDealModal,
    regionalConfig
  } = useCustomerFunnelsData(customerData);

  // UI Layer: opciones de temperatura y traducciones
  const { t, options } = useCustomerFunnelsUI();

  if (loading) return <LoadingMC loading={loading} />;

  return (
    <div className="tw-flex tw-flex-col tw-h-[calc(100vh-250px)] tw-max-h-screen tw-min-h-[400px]">
      <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-pr-1">
        <span className="tw-text-sm tw-font-medium tw-text-gray-950">
          {t('Funnels.title')}
        </span>
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
          <ButtonComponent
            outlined
            text={t('Funnels.create')}
            onClick={() => openDealModal()}
            className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-my-2"
          />

          {customerDeals.length === 0 && !loading && (
            <EmptyScreenListInboxMC
              iconName="briefcase-outline"
              showDescription={false}
              title={t('Funnels.empty')}
              iconClassName="!tw-size-[24px]"
              containerClassName="tw-w-full"
              innerClassName="!tw-rounded-xl !tw-p-6"
            />
          )}

          {customerDeals.map((deal) => (
            <div key={`customer-order-${deal.id}`} className="tw-w-full tw-mb-4">
              <CustomerDealItemMC
                deal={deal}
                options={options}
                regionalConfig={regionalConfig}
                reloadDeals={reloadDeals}
                openModal={openDealModal}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

CustomerFunnelsMC.displayName = 'CustomerFunnelsMC';

export default CustomerFunnelsMC;